import {
  PaginationState,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import React from "react";
import { PaymentMethodRowObj } from "..";
import CreatePaymentMethoddModal from "./CreateRoleMethod";
import DeleteRoleModal from "./DeleteRoleModal";
import Pagination from "components/pagination";
import CustomToolTip from "components/tooltips/new";
import { useNavigate } from "react-router-dom";
import Searchbox from "components/fields/Searchbox";
import { BsArrowDown, BsArrowUp } from "react-icons/bs";
import { RiArrowUpDownFill } from "react-icons/ri";

const columnHelper = createColumnHelper<PaymentMethodRowObj>();

/* ── Status badge helper ── */
const StatusBadge = ({ status }: { status: string }) => {
  if (status === "all") {
    return (
      <span className="rmt-status-badge rmt-status-badge--all">
        <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12"/>
        </svg>
        Full
      </span>
    );
  }
  if (status === "partial") {
    return (
      <span className="rmt-status-badge rmt-status-badge--partial">
        <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
          <line x1="5" y1="12" x2="19" y2="12"/>
        </svg>
        Part
      </span>
    );
  }
  return (
    <span className="rmt-status-badge rmt-status-badge--none">
      <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
      </svg>
      None
    </span>
  );
};

// const statusSortFn = (key: string) => (rowA: any, rowB: any) => {
//   const order: any = { all: 1, partial: 2, none: 3 };
//   const a = rowA.original[key]?.status || "none";
//   const b = rowB.original[key]?.status || "none";
//   return (order[a] || 3) - (order[b] || 3);
// };

const statusSortFn = (key: string) => (rowA: any, rowB: any) => {
  const order: Record<string, number> = {
    all: 1,
    partial: 2,
    none: 3,
  };

  const a = rowA.original[key]?.status || "none";
  const b = rowB.original[key]?.status || "none";

  const result = (order[a] ?? 3) - (order[b] ?? 3);

  if (result !== 0) {
    return result;
  }

  return String(rowA.original.id).localeCompare(
    String(rowB.original.id)
  );
};

/* ── Module columns config — keeps the table DRY ── */
const MODULE_COLS = [
  "dashboard","client","organization","payment","user","payout",
  "payment_method","role","subscription","settlement","schedule_settlement","api",
  "blacklist","whitelist","settings","customers","api_request_logs",
  "crypto","kyc_velocity","fiat","audit_logs",
];

const MODULE_LABELS: Record<string, string> = {
  dashboard: "Dashboard", client: "Client", organization: "Org",
  payment: "Txns", user: "User", payout: "Payout",
  payment_method: "Gateways", role: "Role", subscription: "Subs",
  settlement: "Settlement",schedule_settlement: "Sc Settlement", api: "API", blacklist: "Blacklist",
  whitelist: "Whitelist", settings: "Settings", customers: "Customers",
  api_request_logs: "API Logs", crypto: "Crypto", kyc_velocity: "KYC Vel",
  fiat: "Fiat", audit_logs: "Audit",
};

function RoleMethodTable(props: {
  tableData: any;
  allRoleData:any;
  fetchRole: () => void;
  fetchSingleRole: () => void;
  isLoading: boolean;
  page: number;
  setPage: any;
  totalpage: number;
  totalItems: number;
  currentPage: number;
  pageSize: number;
  setPageSize: any;
  singleRoleData: any;
  onValueChange: (value: string) => void;
}) {
  const {
    tableData, allRoleData,fetchRole, fetchSingleRole, page, setPage,
    currentPage, totalpage, totalItems, pageSize, setPageSize,
    singleRoleData, onValueChange,
  } = props;

  const navigate = useNavigate();
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [data, setData] = React.useState(() => [...(tableData ?? [])]);

  React.useEffect(() => { setData(tableData ?? []); }, [tableData]);

  const handleValueChange = (e: any) => onValueChange(e);

  /* ── Build module columns dynamically ── */
  const moduleColumns = MODULE_COLS.map((key) =>
    columnHelper.accessor(key as any, {
      id: key,
      header: () => <span className="rmt-th">{MODULE_LABELS[key]}</span>,
      cell: (info: any) => <StatusBadge status={info?.row?.original?.[key]?.status} />,
      sortingFn: statusSortFn(key),
      enableSorting: true,
    })
  );

  const columns = [
    columnHelper.accessor("role_name", {
      id: "role_name",
      header: () => <span className="rmt-th">Role Name</span>,
      cell: (info: any) => {
        const name = info?.row?.original?.role_name;
        return (
          <div className="rmt-name-cell">
            <div className="rmt-role-avatar">
              {name?.charAt(0)?.toUpperCase() || "R"}
            </div>
            <span className="rmt-role-name">{name}</span>
          </div>
        );
      },
      sortingFn: (rowA, rowB) => {
        const a = rowA.original.role_name || "";
        const b = rowB.original.role_name || "";
        const result = a.localeCompare(b);
        if (result !== 0) {
          return result;
        }
        return String(rowA.original.id).localeCompare(String(rowB.original.id));
      },
      enableSorting: true,
    }),
    columnHelper.accessor("users" as any, {
      id: "users",
      header: () => <span className="rmt-th">Users</span>,
      cell: (info: any) => {
        const userList: any = info.row.original.users || [];
        const userCount = userList.length;
        return (
          <CustomToolTip
            content={
              userCount > 0 ? (
                <div className="rmt-tooltip-grid">
                  {userList.map((user: any, index: any) => (
                    <span
                      key={index}
                      className="rmt-tooltip-chip rmt-tooltip-chip--clickable"
                      // onClick={() => navigate("/admin/users", { state: { search: user.name } })}
                    >
                      {user.name}
                    </span>
                  ))}
                </div>
              ) : (
                <span className="rmt-no-data">No Users</span>
              )
            }
            trigger={
              <div className={`rmt-count-pill${userCount === 0 ? " rmt-count-pill--empty" : ""}`}>
                <span className="rmt-count-dot" />
                <span>{userCount}</span>
                {userCount > 0 && (
                  <svg width="10" height="10" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5" fill="none">
                    <polyline points="6 9 12 15 18 9"/>
                  </svg>
                )}
              </div>
            }
            header="Assigned Users"
          />
        );
      },
      sortingFn: (rowA, rowB) => {
        const a = (rowA.original.users || []).length;
        const b = (rowB.original.users || []).length;
        return a - b;
      },
      enableSorting: true,
    }),
    ...moduleColumns,
    columnHelper.accessor("id", {
      id: "id",
      header: () => <span className="rmt-th">Actions</span>,
      cell: (info: any) => (
        <div className="rmt-actions">
          <CreatePaymentMethoddModal
            fetchRole={fetchRole}
            id={info?.row?.original?.id}
            data={info.row?.original}
            singleRoleData={singleRoleData}
            fetchSingleRole={fetchSingleRole}
          />
          <CreatePaymentMethoddModal
            fetchRole={fetchRole}
            is_info={true}
            id={info?.row?.original?.id}
            data={info.row?.original}
            singleRoleData={singleRoleData}
            fetchSingleRole={fetchSingleRole}
          />
          {singleRoleData?.[0]?.role?.value?.delete_role && (
            <DeleteRoleModal
              fetchRole={fetchRole}
              id={info?.row?.original?.id}
              data={info?.row?.original}
              roles={tableData}
              name={info?.row?.original?.role_name ?? ""}
              allRoleData={allRoleData}
            />
          )}
        </div>
      ),
      enableSorting: false,
    }),
  ];

  const columnsNonAction = columns.slice(0, columns.length - 1);

  const hasActionPerms =
    singleRoleData?.[0]?.role?.value?.view_role ||
    singleRoleData?.[0]?.role?.value?.edit_role ||
    singleRoleData?.[0]?.role?.value?.delete_role;

  const table = useReactTable({
    data,
    columns: hasActionPerms ? columns : columnsNonAction,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <div className="rmt-card">
      {/* Header */}
      <div className="rmt-header">
        <div className="rmt-header-left">
          <div className="rmt-header-icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            </svg>
          </div>
          <div>
            <h3 className="rmt-title">All Roles</h3>
            <p className="rmt-subtitle">
              {totalItems > 0
                ? `${totalItems} total role${totalItems !== 1 ? "s" : ""}`
                : "No roles found"}
            </p>
          </div>
        </div>

        {/* Legend */}
        <div className="rmt-legend">
          <span className="rmt-legend-item">
            <span className="rmt-legend-dot rmt-legend-dot--all"/>Full access
          </span>
          <span className="rmt-legend-item">
            <span className="rmt-legend-dot rmt-legend-dot--partial"/>Partial
          </span>
          <span className="rmt-legend-item">
            <span className="rmt-legend-dot rmt-legend-dot--none"/>No access
          </span>
        </div>

        <div className="rmt-search-wrap">
          <Searchbox onSearch={handleValueChange} />
        </div>
      </div>

      {/* Table */}
      <div className="rmt-table-wrap">
        {props.isLoading ? (
          <div className="rmt-loading">
            <div className="rmt-spinner"/>
            <p className="rmt-loading-txt">Loading roles…</p>
          </div>
        ) : (
          <table className="rmt-table">
            <thead>
              {table.getHeaderGroups().map((hg) => (
                <tr key={hg.id}>
                  {hg.headers.map((h) => (
                    <th
                      key={h.id}
                      colSpan={h.colSpan}
                      onClick={h.column.getCanSort() ? h.column.getToggleSortingHandler() : undefined}
                      className={`rmt-thead-th${h.column.getCanSort() ? " rmt-thead-th--sort" : ""}`}
                    >
                      <div className="rmt-th-inner">
                        {flexRender(h.column.columnDef.header, h.getContext())}
                        {h.column.getCanSort() && (
                          <span className="rmt-sort-icon">
                            {h.column.getIsSorted() === "asc" ? <BsArrowUp size={11}/> :
                             h.column.getIsSorted() === "desc" ? <BsArrowDown size={11}/> :
                             <RiArrowUpDownFill size={11}/>}
                          </span>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.length > 0 ? (
                table.getRowModel().rows.map((row, ri) => (
                  <tr key={row.id} className={`rmt-tbody-tr${ri % 2 !== 0 ? " rmt-tbody-tr--alt" : ""}`}>
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} className="rmt-tbody-td">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={25}>
                    <div className="rmt-empty">
                      <div className="rmt-empty-icon">
                        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                        </svg>
                      </div>
                      <p className="rmt-empty-title">No roles found</p>
                      <p className="rmt-empty-sub">Try adjusting your search or create a new role</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      <div className="rmt-pagination-wrap">
        <Pagination
          setPage={setPage}
          page={page}
          totalpage={totalpage}
          currentPage={currentPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          arraySize={[50, 100, 200]}
        />
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

        .rmt-card {
          font-family: 'Plus Jakarta Sans', sans-serif;
          background: #ffffff; border: 1px solid #e2e8f0; border-radius: 18px;
          overflow: hidden; box-shadow: 0 2px 8px rgba(15,23,42,0.06);
        }

        /* Header */
        .rmt-header {
          padding: 20px 24px 16px; border-bottom: 1px solid #f1f5f9;
          display: flex; align-items: center; justify-content: space-between;
          gap: 14px; flex-wrap: wrap;
        }
        .rmt-header-left { display: flex; align-items: center; gap: 12px; }
        .rmt-header-icon {
          width: 40px; height: 40px; border-radius: 11px;
          background: linear-gradient(135deg,#fff1f2,#ffe4e6);
          border: 1.5px solid #fecdd3;
          display: flex; align-items: center; justify-content: center;
          color: #e11d48; flex-shrink: 0;
        }
        .rmt-title { font-size: 15px; font-weight: 700; color: #0f172a; letter-spacing: -0.2px; margin-bottom: 2px; line-height: 22px; }
        .rmt-subtitle { font-size: 12px; color: #94a3b8; font-weight: 500; }
        .rmt-search-wrap { min-width: 220px; }

        /* Legend */
        .rmt-legend { display: flex; align-items: center; gap: 14px; flex-wrap: wrap; }
        .rmt-legend-item { display: flex; align-items: center; gap: 5px; font-size: 11.5px; font-weight: 600; color: #64748b; }
        .rmt-legend-dot { width: 8px; height: 8px; border-radius: 50%; }
        .rmt-legend-dot--all     { background: #16a34a; }
        .rmt-legend-dot--partial { background: #d97706; }
        .rmt-legend-dot--none    { background: #e2e8f0; }

        /* Table */
        .rmt-table-wrap { overflow-x: auto; }
        .rmt-table-wrap::-webkit-scrollbar { height: 4px; }
        .rmt-table-wrap::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }
        .rmt-table { width: 100%; border-collapse: collapse; }

        /* Head */
        .rmt-thead-th {
          padding: 10px 12px 8px; background: #f8fafc;
          border-bottom: 1.5px solid #e2e8f0;
          text-align: left; white-space: nowrap; user-select: none;
        }
        .rmt-thead-th--sort { cursor: pointer; }
        .rmt-thead-th--sort:hover { background: #f1f5f9; }
        .rmt-th-inner { display: flex; align-items: center; gap: 4px; }
        .rmt-th { font-size: 10.5px; font-weight: 700; color: #374151; text-transform: uppercase; letter-spacing: 0.4px; }
        .rmt-sort-icon { color: #94a3b8; display: flex; align-items: center; transition: color 0.15s; }
        .rmt-thead-th--sort:hover .rmt-sort-icon { color: #e11d48; }

        /* Rows */
        .rmt-tbody-tr { transition: background 0.12s; }
        .rmt-tbody-tr:hover { background: #f8fafc; }
        .rmt-tbody-tr--alt { background: #fafbfc; }
        .rmt-tbody-tr--alt:hover { background: #f1f5f9; }
        .rmt-tbody-td { padding: 10px 12px; border-bottom: 1px solid #f1f5f9; vertical-align: middle; }

        /* Name cell */
        .rmt-name-cell { display: flex; align-items: center; gap: 9px; }
        .rmt-role-avatar {
          width: 30px; height: 30px; border-radius: 8px; flex-shrink: 0;
          background: linear-gradient(135deg,#fff1f2,#ffe4e6);
          border: 1.5px solid #fecdd3;
          display: flex; align-items: center; justify-content: center;
          font-size: 12px; font-weight: 700; color: #e11d48;
        }
        .rmt-role-name { font-size: 13px; font-weight: 700; color: #0f172a; white-space: nowrap; }

        /* Status badges */
        .rmt-status-badge {
          display: inline-flex; align-items: center; gap: 4px;
          padding: 3px 7px; border-radius: 6px;
          font-size: 10.5px; font-weight: 700; white-space: nowrap;
        }
        .rmt-status-badge--all {
          background: linear-gradient(135deg,#f0fdf4,#dcfce7);
          border: 1.5px solid #bbf7d0; color: #15803d;
        }
        .rmt-status-badge--partial {
          background: linear-gradient(135deg,#fffbeb,#fef3c7);
          border: 1.5px solid #fde68a; color: #d97706;
        }
        .rmt-status-badge--none {
          background: #f8fafc; border: 1.5px solid #e2e8f0; color: #94a3b8;
        }

        /* User count pill */
        .rmt-count-pill {
          display: inline-flex; align-items: center; gap: 5px;
          padding: 4px 10px; border-radius: 8px; cursor: pointer;
          font-size: 12px; font-weight: 700;
          background: linear-gradient(135deg,#eff6ff,#dbeafe);
          border: 1.5px solid #bfdbfe; color: #1d4ed8;
          transition: all .15s;
        }
        .rmt-count-pill:hover { background: #dbeafe; border-color: #93c5fd; }
        .rmt-count-pill--empty { background: #f8fafc; border: 1.5px solid #e2e8f0; color: #94a3b8; cursor: default; }
        .rmt-count-pill--empty:hover { background: #f8fafc; }
        .rmt-count-dot { width: 6px; height: 6px; border-radius: 50%; background: #2563eb; flex-shrink: 0; }
        .rmt-count-pill--empty .rmt-count-dot { background: #cbd5e1; }

        /* Tooltip */
        .rmt-tooltip-grid { display: flex; flex-wrap: wrap; gap: 6px; }
        .rmt-tooltip-chip {
          font-size: 11.5px; font-weight: 600;
          padding: 4px 8px; border-radius: 6px;
          background: #f1f5f9; border: 1px solid #e2e8f0; color: #374151;
        }
        .rmt-tooltip-chip--clickable { cursor: pointer; }
        .rmt-tooltip-chip--clickable:hover { background: #dbeafe; border-color: #bfdbfe; color: #1d4ed8; }
        .rmt-no-data { font-size: 12px; color: #94a3b8; font-style: italic; }

        /* Actions */
        .rmt-actions { display: flex; align-items: center; gap: 6px; }

        /* Empty */
        .rmt-empty {
          display: flex; flex-direction: column; align-items: center;
          justify-content: center; padding: 56px 24px; gap: 10px;
        }
        .rmt-empty-icon {
          width: 52px; height: 52px; border-radius: 13px;
          background: #f8fafc; border: 1.5px solid #e2e8f0;
          display: flex; align-items: center; justify-content: center; color: #cbd5e1;
        }
        .rmt-empty-title { font-size: 14px; font-weight: 700; color: #64748b; }
        .rmt-empty-sub { font-size: 12.5px; color: #94a3b8; font-weight: 500; text-align: center; }

        /* Loading */
        .rmt-loading {
          display: flex; flex-direction: column; align-items: center;
          justify-content: center; padding: 56px; gap: 12px;
        }
        .rmt-spinner {
          width: 28px; height: 28px;
          border: 3px solid #e2e8f0; border-top-color: #e11d48;
          border-radius: 50%; animation: rmtSpin 0.7s linear infinite;
        }
        @keyframes rmtSpin { to { transform: rotate(360deg); } }
        .rmt-loading-txt { font-size: 13px; color: #94a3b8; font-weight: 500; }

        /* Pagination */
        .rmt-pagination-wrap { padding: 12px 16px; border-top: 1px solid #f1f5f9; background: #fafbfc; }

        @media (max-width: 700px) {
          .rmt-header { flex-direction: column; align-items: flex-start; }
          .rmt-search-wrap { width: 100%; }
          .rmt-thead-th, .rmt-tbody-td { padding: 8px 10px; }
        }
      `}</style>
    </div>
  );
}

export default RoleMethodTable;